import {guessPlayerProvider} from '@common/player/utils/guess-player-provider';
import {VideoPlayer} from '@common/player/ui/video-player/video-player';
import {VideoThumbnail} from '@app/download/video-thumbnail';
import {IconButton} from '@ui/buttons/icon-button';
import {MediaPlayIcon} from '@ui/icons/media/media-play';
import React, {memo, useEffect, useRef, useState} from 'react';
import {Title} from '@app/titles/models/title';
import {Episode} from '@app/titles/models/episode';
import {VideoPlayerSkeleton} from '@app/download/video-player-skeleton';
import {MediaItem} from '@common/player/media-item';
import {useLogVideoPlay} from '@app/download/requests/use-log-video-play';
import {PlayerActions} from '@common/player/hooks/use-player-actions';
// On n'utilise plus getDownloadLink() car on ne navigue plus vers une autre page
// import {getDownloadLink} from '@app/download/download-page/get-download-link';
import {Trans} from '@ui/i18n/trans';
import {EpisodeSelector} from '@app/download/download-page/episode-selector';
import {Lien} from '@app/titles/models/lien';
import axios from 'axios';
import {isSameMedia} from '@common/player/utils/is-same-media';
import {useAuth} from '@common/auth/use-auth';
import {getDownloadLink} from '@app/download/download-page/get-download-link';
import {useNavigate} from 'react-router-dom';

interface Props {
  video: Lien;
  relatedVideos?: Lien[];
  autoPlay?: boolean;
  title?: Title;
  episode?: Episode;
  mediaItemId?: string;
  logPlays?: boolean;
  showEpisodeSelector?: boolean;
}

/**
 * Fonction asynchrone qui permet de "débrider" la vidéo.
 */
const MediaDebrider = async (src: string) => {
  try {
    const response = await axios.post('/api/v1/debriding', {id: src, type: 1});
    if (response.data.url) {
      response.data.id = src;
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error debriding media:', error);
    return null;
  }
};

export const DownloadVideoPlayer = memo((props: Props) => {
  const {autoPlay, title, episode, video} = props;
  const [videoPlayer, setVideoPlayer] = useState(props);
  const {user} = useAuth();
  const [loading, setLoading] = useState(false);
  const handleDebrid = async () => {
    setLoading(true);
    if (user?.IsPremium) {
      const data = await MediaDebrider(video.id as unknown as string);
      if (data) {
        setVideoPlayer(prevState => ({
          ...prevState,
          video: {
            ...prevState.video,
            src: data.url,
            type: 'stream',
          },
        }));
      }
    }
    setLoading(false);
  };
  if (
    videoPlayer.video.type === 'video' ||
    videoPlayer.video.type === 'stream' ||
    (videoPlayer.video.type === 'embed' &&
      videoPlayer.video.src.includes('youtube'))
  ) {
    return <NativeVideoPlayer {...videoPlayer} />;
  } else if (videoPlayer.video.type === 'infofile') {
    return (
      <div className="relative">
        <VideoThumbnail
          title={title}
          episode={episode}
          video={videoPlayer.video}
          fallback={<div className="aspect-video w-full bg-fg-base/4" />}
        />
        <div
          onClick={() => handleDebrid()}
          className="absolute left-0 top-0 flex h-full w-full items-center justify-center"
        >
          <IconButton
            variant="flat"
            color="primary"
            size="lg"
            disabled={loading}
          >
            {loading ? <span>Loading...</span> : <MediaPlayIcon />}
          </IconButton>
        </div>
      </div>
    );
  } else if (videoPlayer.video.type === 'embed') {
    return <EmbedPlayer src={videoPlayer.video.src} autoPlay={autoPlay} />;
  } else if (videoPlayer.video.type === 'external') {
    return (
      <div className="relative">
        <VideoThumbnail
          title={title}
          episode={episode}
          video={videoPlayer.video}
          fallback={<div className="aspect-video w-full bg-fg-base/4" />}
        />
        <div
          className="absolute left-0 top-0 flex h-full w-full items-center justify-center"
          onClick={() => window.open(videoPlayer.video.src, '_blank')}
        >
          <IconButton variant="flat" color="primary" size="lg">
            <MediaPlayIcon />
          </IconButton>
        </div>
      </div>
    );
  }

  return <VideoPlayerSkeleton />;
});

interface EmberPlayerProps {
  src: string;
  autoPlay?: boolean;
}

const EmbedPlayer = memo(({src, autoPlay}: EmberPlayerProps) => {
  let finalSrc = '';
  try {
    const url = src.includes('<iframe') ? src.match(/src="([^"]*)"/)?.[1] : src;
    const parsed = new URL(url || '');
    parsed.searchParams.set('autoplay', autoPlay ? '1' : '0');
    finalSrc = parsed.toString();
  } catch {}

  if (!finalSrc) {
    return (
      <div className="flex aspect-video w-full items-center justify-center">
        <div className="rounded-panel border p-10">
          <Trans message="There was an issue playting this video." />
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={finalSrc}
      className="aspect-video w-full"
      allowFullScreen
      allow="autoplay; encrypted-media; picture-in-picture;"
    />
  );
});

/**
 * Composant qui affiche le player natif et permet de changer dynamiquement la source
 * du player (via updateEpisode) au lieu de naviguer vers une nouvelle page.
 */
function NativeVideoPlayer({
  video,
  title,
  episode,
  mediaItemId,
  relatedVideos,
  autoPlay,
  logPlays,
  showEpisodeSelector,
}: Props) {
  const playerRef = useRef<PlayerActions>(null!);
  const {user} = useAuth();
  const navigate = useNavigate();
  const logVideoPlay = useLogVideoPlay(playerRef, {enabled: logPlays});
  const mediaItem = videoToMediaItem(video, mediaItemId);

  const related = relatedVideos?.map(v => videoToMediaItem(v)) ?? [];
  // La navigation n'est plus utilisée ici
  // const navigate = useNavigate();

  /**
   * Fonction qui met à jour la source du player avec le nouvel épisode.
   * Cette fonction crée un nouvel objet MediaItem à partir du nouvel épisode
   * et remplace la file d'attente du player.
   */
  const updateEpisode = (newEpisode: Episode) => {
    if (user?.IsPremium) {
      const newEpisodeDebrid = MediaDebrider(
        newEpisode?.primary_video?.id as unknown as string,
      ).then(data => {
        if (data) {
          return {
            ...newEpisode,
            lien: data.url,
          };
        }
        return newEpisode;
      });
      newEpisodeDebrid.then(newEpisode => {
        const newMediaItem: MediaItem = {
          id: newEpisode.id, // Assurez-vous que l'épisode possède un identifiant unique
          provider: guessPlayerProvider(newEpisode.lien),
          src: newEpisode.lien,
          poster: (newEpisode as any).thumbnail || '',
          meta: newEpisode,
        };
        playerRef.current.overrideQueue([newMediaItem, ...related]);
      });
    } else {
      const newMediaItem: MediaItem = {
        id: newEpisode.id, // Assurez-vous que l'épisode possède un identifiant unique
        provider: guessPlayerProvider(newEpisode.lien),
        src: newEpisode.lien,
        poster: (newEpisode as any).thumbnail || '',
        meta: newEpisode,
      };
      playerRef.current.overrideQueue([newMediaItem, ...related]);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        logVideoPlay();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [logVideoPlay]);

  return (
    <VideoPlayer
      apiRef={playerRef}
      id="player"
      queue={[mediaItem, ...related]}
      autoPlay={autoPlay}
      onBeforePlayNext={nextMedia => {
        if (nextMedia && !isSameMedia(mediaItem, nextMedia)) {
        }
        return true;
      }}
      onDestroy={() => logVideoPlay()}
      listeners={{
        playbackEnd: () => logVideoPlay(),
        beforeCued: ({previous}) => {
          if (previous) {
            logVideoPlay();
          }
        },
      }}
      rightActions={
        showEpisodeSelector && title && episode ? (
          <EpisodeSelector
            title={title}
            currentEpisode={episode}
            onSelected={episode => {
              navigate(getDownloadLink(undefined, episode));
              updateEpisode(episode);
            }}
          />
        ) : undefined
      }
    />
  );
}

/**
 * Fonction utilitaire qui transforme un objet Lien en MediaItem.
 */
function videoToMediaItem(video: Lien, mediaItemId?: string): MediaItem {
  return {
    id: mediaItemId || video.id,
    provider: guessPlayerProvider(video.src),
    src: video.src,
    poster: video.thumbnail,
    meta: video,
    initialTime: video.latest_play?.time_watched ?? undefined,
    captions: video.captions?.map(caption => ({
      id: caption.id,
      src: caption.url,
      label: caption.name,
      language: caption.language,
    })),
  };
}
